<template>
    <div class="qr-code-wrapper">
        <img 
            v-if="qrCodeUrl && !isLoading" 
            :src="qrCodeUrl" 
            :alt="`QR Code for item ${selectedItem?.id || 'unknown'}`"
            :class="element.attributes.classes" 
            :style="element.styles" 
            @error="handleImageError"
            :id="element.attributes.id" 
            :ref="element.attributes.id" 
            :name="element.attributes.name" 
        />
        <div v-else-if="isLoading" class="qr-loading">
            <p>Generating QR code...</p>
        </div>
        <div v-else class="qr-placeholder">
            <p>No QR code available</p>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, onUnmounted } from 'vue';
import { vItemStore } from '@/store/pinia_modules/vItemStore';
import { Container } from '@/types/customPage';
import { getAction } from '@/api/action.api';
import { authStore } from '@/store/pinia_modules/authStore';

defineProps<{
    element: Container;
}>();

const store = vItemStore();
const campaignID = authStore().campaignID;

const selectedItem = computed(() => store.getSelectedItem);
const qrCodeUrl = computed(() => selectedItem.value?.qrCodeUrl || '');
const qrActionID = computed(() => selectedItem.value?.qrActionID || '');

const isLoading = ref(false);
const pollingInterval = ref<number | null>(null);
const maxPollingAttempts = 30; // 30 seconds max
let pollingAttempts = 0;

const startPolling = async () => {
    if (!qrActionID.value || !campaignID) {
        console.warn('Missing qrActionID or campaignID for polling');
        return;
    }
    
    isLoading.value = true;
    pollingAttempts = 0;
    
    const pollAction = async () => {
        try {
            pollingAttempts++;
            
            if (pollingAttempts > maxPollingAttempts) {
                console.warn('Max polling attempts reached for QR code generation');
                isLoading.value = false;
                clearPolling();
                return;
            }
            
            const action = await getAction(campaignID!, qrActionID.value);
            console.log(`Polling attempt ${pollingAttempts}: Action status: ${action.status}`);
            
            if (action.status === 'complete') {
                console.log('QR code generation completed');
                isLoading.value = false;
                clearPolling();
                
                // Refresh the items to get the updated QR code URL
                await store.getItems();
            } else if (action.status === 'failed' || action.status === 'error') {
                console.error('QR code generation failed:', action.error);
                isLoading.value = false;
                clearPolling();
            } else {
                // Continue polling
                pollingInterval.value = window.setTimeout(pollAction, 1000);
            }
        } catch (error) {
            console.error('Error polling for QR code:', error);
            isLoading.value = false;
            clearPolling();
        }
    };
    
    // Start the first poll
    await pollAction();
};

const clearPolling = () => {
    if (pollingInterval.value) {
        clearTimeout(pollingInterval.value);
        pollingInterval.value = null;
    }
};

// Watch for changes in the selected item or QR code URL
watch([selectedItem, qrCodeUrl], async ([newItem, newQrCodeUrl]) => {
    // Clear any existing polling
    clearPolling();
    
    // If we have a QR code URL, no need to poll
    if (newQrCodeUrl) {
        isLoading.value = false;
        return;
    }
    
    // If we have an action ID but no QR code, start polling
    if (newItem?.qrActionID && !newQrCodeUrl) {
        await startPolling();
    }
}, { immediate: true });

const handleImageError = (event: Event) => {
    console.error('Failed to load QR code image:', event);
    // You could emit an error event here if needed
};

// Clean up on component unmount
onUnmounted(() => {
    clearPolling();
});
</script>

<style scoped>
.qr-code-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 100px;
}

.qr-loading {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 1rem;
    color: #666;
    font-style: italic;
}

.qr-placeholder {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 1rem;
    color: #666;
    font-style: italic;
}
</style>
